import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
import { DatosPorDia } from '../../services/gestionService';

interface GraficoUsoProps {
  datos: DatosPorDia[];
  isLoading: boolean;
  horasDisponibles?: number;
}

interface DiaDetalle {
  fecha: string;
  maquinas: Array<{
    codigo: string;
    id: number;
    horas: number;
  }>;
}

interface ResumenMaquina {
  codigo: string;
  id: number;
  horasTotales: number;
  porcentajePromedio: number;
}

type VistaUso = 'tabla' | 'gantt';

export const GraficoUso: React.FC<GraficoUsoProps> = ({
  datos,
  isLoading,
  horasDisponibles = 24,
}) => {
  const { t } = useTranslation();
  const [vista, setVista] = useState<VistaUso>('tabla');
  const [diasMap, setDiasMap] = useState<Map<string, DiaDetalle>>(new Map());
  const [maquinas, setMaquinas] = useState<string[]>([]);
  const [mesActual, setMesActual] = useState(new Date());
  const [, setResumenMaquinas] = useState<Map<string, ResumenMaquina>>(new Map());

  useEffect(() => {
    if (datos && datos.length > 0) {
      // Agrupar datos por fecha
      const mapa = new Map<string, DiaDetalle>();
      const maquinasSet = new Set<string>();
      const resumenMap = new Map<string, ResumenMaquina>();

      datos.forEach((item) => {
        const fecha = item.fecha;
        if (!mapa.has(fecha)) {
          mapa.set(fecha, { fecha, maquinas: [] });
        }
        mapa.get(fecha)!.maquinas.push({
          codigo: item.maquina_codigo,
          id: item.maquina_id,
          horas: item.horas,
        });
        maquinasSet.add(item.maquina_codigo);

        // Acumular para resumen
        if (!resumenMap.has(item.maquina_codigo)) {
          resumenMap.set(item.maquina_codigo, {
            codigo: item.maquina_codigo,
            id: item.maquina_id,
            horasTotales: 0,
            porcentajePromedio: 0,
          });
        }
        resumenMap.get(item.maquina_codigo)!.horasTotales += item.horas;
      });

      // Calcular porcentaje promedio para cada máquina
      resumenMap.forEach((resumen) => {
        resumen.porcentajePromedio = Math.round((resumen.horasTotales / horasDisponibles) * 100);
      });

      setDiasMap(mapa);
      setMaquinas(Array.from(maquinasSet).sort());
      setResumenMaquinas(resumenMap);

      // Establecer el mes del primer dato si existe
      if (datos.length > 0) {
        const [year, month, day] = datos[0].fecha.split('-').map(Number);
        setMesActual(new Date(year, month - 1, day));
      }
    }
  }, [datos, horasDisponibles]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (diasMap.size === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.no_data')}</div>
      </div>
    );
  }

  const handleMesAnterior = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1));
  };

  const handleMesSiguiente = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1));
  };

  return (
    <div className="w-full space-y-4">
      {/* Toggle de vistas */}
      <div className="flex gap-2 border-b border-gray-200 pb-4">
        <button
          onClick={() => setVista('tabla')}
          className={`px-4 py-2 rounded font-medium transition-colors ${
            vista === 'tabla'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 5a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2H9z" />
          </svg>
          {t('management.usage')}
        </button>
        <button
          onClick={() => setVista('gantt')}
          className={`px-4 py-2 rounded font-medium transition-colors ${
            vista === 'gantt'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Gantt
        </button>
      </div>

      {/* Navegador de mes */}
      <div className="flex items-center justify-center gap-4 bg-white rounded-lg shadow p-4">
        <button
          onClick={handleMesAnterior}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Mes anterior"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
          {mesActual.toLocaleDateString(i18n.language === 'pt-BR' ? 'pt-BR' : 'es-CL', { month: 'long', year: 'numeric' })}
        </div>

        <button
          onClick={handleMesSiguiente}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Mes siguiente"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Vista Calendario */}
      {vista === 'tabla' && (
        <CalendarioUso
          mesActual={mesActual}
          diasMap={diasMap}
          maquinas={maquinas}
          horasDisponibles={horasDisponibles}
        />
      )}

      {/* Vista Gantt */}
      {vista === 'gantt' && (
        <GanttUso
          mesActual={mesActual}
          diasMap={diasMap}
          maquinas={maquinas}
          horasDisponibles={horasDisponibles}
        />
      )}
    </div>
  );
};

// ====================================
// VISTA CALENDARIO - DÍAS COLOREADOS
// ====================================

interface CalendarioUsoProps {
  mesActual: Date;
  diasMap: Map<string, DiaDetalle>;
  maquinas: string[];
  horasDisponibles: number;
}

const CalendarioUso: React.FC<CalendarioUsoProps> = ({
  mesActual,
  diasMap,
  maquinas: _maquinas,
  horasDisponibles: _horasDisponibles,
}) => {
  const { t } = useTranslation();
  const year = mesActual.getFullYear();
  const month = mesActual.getMonth();
  const primerDia = new Date(year, month, 1).getDay();
  const ultimoDia = new Date(year, month + 1, 0).getDate();

  const [popupDia, setPopupDia] = React.useState<number | null>(null);
  const [, setPopupPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Cerrar popup al hacer click fuera
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPopupDia(null);
      }
    };

    if (popupDia !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [popupDia]);

  const getColorForPorcentaje = (horas: number, horasDisponiblesDia: number): string => {
    const porcentaje = horasDisponiblesDia > 0 ? (horas / horasDisponiblesDia) * 100 : 0;
    if (porcentaje === 0) return 'bg-gray-50 border-gray-300';
    if (porcentaje < 25) return 'bg-red-400 border-red-500';
    if (porcentaje < 50) return 'bg-orange-400 border-orange-500';
    if (porcentaje < 75) return 'bg-yellow-300 border-yellow-400';
    if (porcentaje < 100) return 'bg-green-400 border-green-500';
    return 'bg-green-500 border-green-600'; // >100% es verde más oscuro (excepcional)
  };

  // Calcular total de horas por día y horas disponibles (máquinas que trabajaron * 8.5 horas)
  const horasDia: { [day: number]: { total: number; disponibles: number } } = {};
  for (let dia = 1; dia <= ultimoDia; dia++) {
    const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const diaData = diasMap.get(fechaStr);
    if (diaData && diaData.maquinas.length > 0) {
      const totalHoras = diaData.maquinas.reduce((sum, m) => sum + m.horas, 0);
      const horasDisponiblesDia = diaData.maquinas.length * 8.5;
      horasDia[dia] = { total: totalHoras, disponibles: horasDisponiblesDia };
    } else {
      horasDia[dia] = { total: 0, disponibles: 0 };
    }
  }

  const dias = [];
  // Agregar días vacíos al inicio
  for (let i = 0; i < primerDia; i++) {
    dias.push(null);
  }
  // Agregar días del mes
  for (let dia = 1; dia <= ultimoDia; dia++) {
    dias.push(dia);
  }

  return (
    <div ref={containerRef} className="bg-white rounded-lg shadow p-6">
      <div className="grid grid-cols-7 gap-2 mb-6">
        {/* Encabezados de días de semana */}
        {(t('management.day_names', { returnObjects: true }) as string[]).map((dayName) => (
          <div
            key={dayName}
            className="text-center font-semibold text-gray-600 text-sm py-2"
          >
            {dayName}
          </div>
        ))}

        {/* Días del mes */}
        {dias.map((dia, idx) => {
          if (dia === null) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }

          const horas = horasDia[dia]?.total || 0;
          const horasDisponiblesDia = horasDia[dia]?.disponibles || 0;
          const porcentaje = horasDisponiblesDia > 0 ? (horas / horasDisponiblesDia) * 100 : 0;
          const tieneDatos = horas > 0;
          const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const diaData = diasMap.get(fechaStr);

          const handleClickDia = (e: React.MouseEvent) => {
            if (popupDia === dia) {
              setPopupDia(null);
            } else {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setPopupPos({ x: rect.left, y: rect.top });
              setPopupDia(dia);
            }
          };

          return (
            <div key={dia} className="relative">
              <div
                onClick={handleClickDia}
                className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center cursor-pointer hover:shadow-lg transition-shadow relative ${getColorForPorcentaje(horas, horasDisponiblesDia)}`}
              >
                {porcentaje >= 100 && (
                  <div className="absolute top-1 right-1">
                    <svg className="w-4 h-4 text-yellow-400 fill-current drop-shadow-md" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                )}
                <div className="text-sm font-bold text-gray-900">{dia}</div>
                {tieneDatos && (
                  <div className="text-xs text-gray-700 font-semibold">
                    {Math.round(porcentaje)}%
                  </div>
                )}
              </div>

              {/* Popup con detalles */}
              {popupDia === dia && diaData && diaData.maquinas.length > 0 && (
                <div className="absolute z-20 bg-white border border-gray-300 rounded-lg shadow-xl p-3 mt-2 w-56 -left-24">
                  <div className="font-semibold text-gray-900 mb-2 text-sm">
                    {new Date(year, month, dia).toLocaleDateString(i18n.language === 'pt-BR' ? 'pt-BR' : 'es-CL', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>

                  <div className="space-y-1 mb-3 border-t border-gray-200 pt-2">
                    {diaData.maquinas.map((maquina) => (
                      <div key={maquina.id} className="flex justify-between text-xs text-gray-700">
                        <span className="font-medium">{maquina.codigo}</span>
                        <span className="text-gray-600">{maquina.horas.toFixed(1)}h</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-sm">
                    <span className="text-gray-900">{t('management.total_label')}</span>
                    <span className="text-gray-900">{horas.toFixed(1)}h</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {t('management.available')}: {horasDisponiblesDia.toFixed(1)}h
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="border-t border-gray-200 pt-4 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-50 border border-gray-300 rounded" />
          <span className="text-gray-600">{t('management.legend_no_data')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-400 border border-red-500 rounded" />
          <span className="text-gray-600">{t('management.legend_very_low')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange-400 border border-orange-500 rounded" />
          <span className="text-gray-600">{t('management.legend_low')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-300 border border-yellow-400 rounded" />
          <span className="text-gray-600">{t('management.legend_medium')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-400 border border-green-500 rounded" />
          <span className="text-gray-600">{t('management.legend_good')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 border border-green-600 rounded flex items-center justify-center relative">
            <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </div>
          <span className="text-gray-600">{t('management.legend_exceptional')} ★</span>
        </div>
      </div>
    </div>
  );
};

// ====================================
// VISTA GANTT - HEATMAP DE USO DIARIO
// ====================================

interface GanttUsoProps {
  mesActual: Date;
  diasMap: Map<string, DiaDetalle>;
  maquinas: string[];
  horasDisponibles: number;
}

const GanttUso: React.FC<GanttUsoProps> = ({
  mesActual,
  diasMap,
  maquinas,
  horasDisponibles: _horasDisponibles,
}) => {
  const { t } = useTranslation();
  const year = mesActual.getFullYear();
  const month = mesActual.getMonth();
  const ultimoDia = new Date(year, month + 1, 0).getDate();

  const getColorForPorcentaje = (horas: number, disponibles: number): string => {
    const porcentaje = disponibles > 0 ? (horas / disponibles) * 100 : 0;

    if (porcentaje === 0) return '#f3f4f6'; // gris claro
    if (porcentaje < 25) return '#f87171'; // rojo claro
    if (porcentaje < 50) return '#fb923c'; // naranja
    if (porcentaje < 75) return '#fbbf24'; // amarillo
    if (porcentaje < 100) return '#4ade80'; // verde
    return '#16a34a'; // verde oscuro (>100%)
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
      <div className="flex">
        {/* Columna de máquinas */}
        <div className="flex flex-col">
          <div className="h-10 bg-gray-100 border-b border-r border-gray-300 flex items-center px-3 font-semibold text-sm text-gray-700 w-32">
            {t('management.machines_header')}
          </div>
          {maquinas.map((maquina) => (
            <div
              key={maquina}
              className="h-10 bg-white border-b border-r border-gray-300 flex items-center px-3 text-sm font-medium text-gray-900 w-32"
            >
              {maquina}
            </div>
          ))}
        </div>

        {/* Grid de días */}
        <div className="flex">
          {/* Encabezado de días */}
          <div className="flex flex-col">
            <div className="flex">
              {Array.from({ length: ultimoDia }, (_, i) => i + 1).map((dia) => (
                <div
                  key={`header-${dia}`}
                  className="h-10 w-8 bg-gray-100 border-b border-r border-gray-300 flex items-center justify-center font-semibold text-xs text-gray-700"
                >
                  {dia}
                </div>
              ))}
            </div>

            {/* Celdas de datos */}
            {maquinas.map((maquina) => (
              <div key={maquina} className="flex">
                {Array.from({ length: ultimoDia }, (_, i) => i + 1).map((dia) => {
                  const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                  const diaData = diasMap.get(fechaStr);
                  const maquinaData = diaData?.maquinas.find((m) => m.codigo === maquina);
                  const horas = maquinaData?.horas || 0;
                  // Para Gantt: comparar horas individuales contra 8.5 horas disponibles por máquina
                  const horasDisponiblesMaquina = 8.5;
                  const porcentaje = horasDisponiblesMaquina > 0 ? (horas / horasDisponiblesMaquina) * 100 : 0;

                  return (
                    <div
                      key={`${maquina}-${dia}`}
                      className="h-10 w-8 border-b border-r border-gray-300 flex items-center justify-center cursor-pointer group relative"
                      style={{ backgroundColor: getColorForPorcentaje(horas, horasDisponiblesMaquina) }}
                      title={horas > 0 ? `${maquina} - ${horas}h (${Math.round(porcentaje)}%)` : ''}
                    >
                      {horas > 0 && (
                        <span className="text-xs font-bold text-gray-900">{Math.round(porcentaje)}</span>
                      )}
                      {/* Tooltip */}
                      {horas > 0 && (
                        <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                          {maquina}: {horas}h / {horasDisponiblesMaquina}h ({Math.round(porcentaje)}%)
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-gray-900 border-t-gray-900 border-r-transparent border-b-transparent border-l-transparent" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leyenda de colores */}
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded" />
          <span className="text-gray-600">{t('management.legend_no_data')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-400 rounded" />
          <span className="text-gray-600">{t('management.legend_very_low')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange-400 rounded" />
          <span className="text-gray-600">{t('management.legend_low')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-300 rounded" />
          <span className="text-gray-600">{t('management.legend_medium')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-400 rounded" />
          <span className="text-gray-600">{t('management.legend_good')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#16a34a' }} />
          <span className="text-gray-600">{t('management.legend_exceptional')}</span>
        </div>
      </div>
    </div>
  );
};
